<template>
    <div>
        <x-data-table
            class="no-row-hover double-rows cell-vertical-align-top cell-padding-vertical"
            :table-class="tableClass"
            :headers="headers"
            :items="lines"
            hide-actions
        >
            <template v-slot:no-data>
                <p class="error-message text-xs-center">No signed lines found</p>
            </template>

            <template v-slot:items="props">
                <tr>
                    <td>
                        {{ props.item.firm_order_offer.insurer_company ? props.item.firm_order_offer.insurer_company.name : props.item.firm_order_offer.captive_insurer_company.name }}
                        <template v-if="props.item.underwriter_reference"><br>Reference: {{ props.item.underwriter_reference }}</template>
                        <template v-if="props.item.risk_code"><br>Risk code: {{ props.item.risk_code }}</template>
                        <template v-if="props.item.insurer_role"><br>Role: {{ props.item.insurer_role }}</template>
                        <br>
                    </td>

                    <td class="text-xs-right">
                        {{ props.item.brokerage | percent }}
                    </td>

                    <td class="text-xs-right">
                        {{ props.item.line_amount | currency(currencyCode) }}
                        <br>
                        <x-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <span v-on="on">
                                    {{ props.item.line_percent_display | percent(4) }}
                                </span>
                            </template>

                            <span>{{ props.item.line_percent | percent(12) }}</span>
                        </x-tooltip>
                    </td>
                    <td class="text-xs-right" v-if="declarationHasWaitingPeriodInterest">
                        <x-tooltip top>
                            <template v-slot:activator="{ on }">
                                <div v-on="on">
                                    {{ props.item.maximum_insured_exposure | currency(currencyCode) }}
                                </div>
                            </template>
                            <p>
                                Written line: {{ props.item.line_amount | currency(currencyCode) }}
                            </p>
                            <p>
                                Interest rate: {{ props.item.firm_order_offer.firm_order.waiting_period_interest_rate | percent }}
                            </p>
                            <p>
                                Waiting period interest: {{ props.item.waiting_period_interest | currency(currencyCode) }}
                            </p>
                            <p>
                                Total: {{ props.item.maximum_insured_exposure | currency(currencyCode) }}
                            </p>
                        </x-tooltip>
                    </td>

                    <td class="text-xs-right">
                        <div v-if="props.item.price || props.item.price === 0">
                            {{ props.item.price }} BPS
                            <template v-if="['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(props.item.offer_type) && (props.item.percentage_of_margin || props.item.percentage_of_margin === 0)">
                                <br>({{ props.item.percentage_of_margin | percent }} of {{ typeOfMargin(props.item.offer_type) }} margin)
                            </template>
                        </div>
                        <div v-else-if="props.item.flat_fee || props.item.flat_fee === 0">
                            {{ props.item.flat_fee | currency(currencyCode) }}
                        </div>
                    </td>

                    <td v-if="showFinalPremium" class="text-xs-right">
                        <div v-if="props.item.final_premium">
                            {{ props.item.final_premium | currency(currencyCode) }}
                        </div>
                    </td>

                    <td v-if="!showFinalPremium && isClassicDeclaration" class="text-xs-right">
                        <div v-if="props.item.classic_estimated_premium">
                            {{ Math.round(props.item.classic_estimated_premium) | currency(currencyCode) }}
                        </div>
                    </td>

                    <td v-if="showEdit" class="text-xs-right">
                        <x-btn
                            v-if="canEdit(props.item)"
                            color="tertiary"
                            class="square-icon extra-small"
                            @click="editLine(props.item)"
                        >
                            <i class="material-icons">edit</i>
                        </x-btn>
                    </td>
                </tr>
                <tr>
                    <td class="note-cell" :colspan="showEdit ? 8 : 7">
                        <span v-if="props.item.security_id">Security: {{ props.item.security.name }}</span>
                    </td>
                </tr>
            </template>
        </x-data-table>

        <x-dialog v-if="showEdit" v-model="showEditLineDialog" persistent max-width="512px">
            <x-form ref="editLineForm" @submit.prevent="submitEditLine">
                <x-container fluid>
                    <x-layout>
                        <x-flex xs12>
                            <h2>Edit signed line</h2>
                        </x-flex>
                    </x-layout>

                    <x-layout mt-3>
                        <x-flex xs12>
                            <x-text-field
                                v-model="underwriterReference"
                                label="Underwriter reference"
                                placeholder=" "
                                :rules="[v => ruleRequired(v, 'Underwriter reference')]"
                                outline
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout class="response-actions" justify-space-between>
                        <x-flex xs6 class="text-xs-left">
                            <x-btn
                                class="ma-0"
                                color="tertiary"
                                small
                                :disabled="editLineLoading"
                                @click="showEditLineDialog = false"
                            >
                                Cancel
                            </x-btn>
                        </x-flex>

                        <x-flex xs6 class="text-xs-right">
                            <x-btn
                                class="ma-0"
                                color="primary"
                                small
                                type="submit"
                                :loading="editLineLoading"
                            >
                                Accept
                            </x-btn>
                        </x-flex>
                    </x-layout>
                </x-container>
            </x-form>
        </x-dialog>
    </div>
</template>

<script>
    import rules from '~/mixins/rules';
    import waitingPeriodInterest from '~/mixins/waitingPeriodInterest';
    export default {
        name: 'SignedLinesTable',
        mixins: [rules],
        props: {
            lines: {
                type: Array,
                default: []
            },
            facility: {
                type: Object
            },
            currencyCode: {
                type: String
            },
            showFinalPremium: {
                type: Boolean
            },
            showEdit: {
                type: Boolean
            },
            tableClass: {
                type: String,
                default: ''
            },
            isStandalonePolicy: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                showEditLineDialog: false,
                lineId: null,
                underwriterReference: null,
                submissionId: null,
                editLineLoading: false
            }
        },
        computed: {
            headers() {
                const headers = [
                    {
                        text: 'insurer / security',
                        value: 'insurer_company.name'
                    },
                    {
                        text: 'brokerage',
                        value: 'brokerage',
                        align: 'right'
                    },
                    {
                        text: 'written line',
                        value: 'line_percent',
                        align: 'right'
                    },
                ];
                if (this.declarationHasWaitingPeriodInterest) {
                    headers.push({
                        text: 'maximum insured exposure',
                        value: 'max_insured_exposure',
                        align: 'right'
                    });
                }
                headers.push({

                    text: 'premium rate',
                    align: 'right'

                });
                if (this.showFinalPremium) {
                    headers.push({
                        text: 'final premium',
                        value: 'final_premium',
                        align: 'right'
                    });
                } else if (this.isClassicDeclaration) {
                        headers.push({
                            text: 'Tepfin classic estimated premium',
                            value: 'classic_estimated_premium',
                            align: 'right'
                        });
                    }

                if (this.showEdit) {
                    headers.push({
                        text: '',
                        value: '',
                        align: 'right'
                    });
                }

                return headers;
            },
            isClassicDeclaration() {
                const firstLine = this.lines[0];

                return firstLine.declaration.classic_declaration_id;
            },
            declarationHasWaitingPeriodInterest() {
                return this.facility && waitingPeriodInterest.submissionHasWaitingPeriodInterest(this.facility.waiting_period_interest);
            },
        },
        methods: {
            editLine(line) {
                this.lineId = line.id;
                this.underwriterReference = line.underwriter_reference;
                this.submissionId = line.declaration.submission_id;
                this.showEditLineDialog = true;
            },
            async submitEditLine() {
                if (this.$refs.editLineForm.validate()) {
                    this.editLineLoading = true;

                    const requestData = {
                        underwriter_reference: this.underwriterReference
                    };

                    await axios.put(this.$root.api(`insurer/declaration/${this.submissionId}/line/${this.lineId}`), requestData).then(() => {
                        this.$router.go();
                    }).catch(error => {
                        this.editLineLoading = false;
                    });
                }
            },
            canEdit(offer) {
                return this.$root.user.company_id === offer.insurer_company_id;
            },
            typeOfMargin(type) {
                if (type === 'percentage_of_net_margin') {
                    return 'net';
                } else if (type === 'percentage_of_gross_margin') {
                    return 'gross';
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
    .x-table.x-datatable tbody tr td.note-cell {
        height: auto;
        padding-top: 0;
        padding-bottom: 0;

        > span {
            display: inline-block;
            padding-bottom: 8px;
        }
    }
</style>
